<template>
  <!-- Gated on DRIVERS, the permission the `car` resource itself uses: there
       is no CARS/VEHICLES permission, and this register is entered by fleet
       staff even though its cost is what feeds expense account 51313.
       Deliberately WITHOUT <finance-section-nav />: that nav filters its tabs
       by finance permissions, so a fleet clerk would get an empty tab bar.
       The shared fin-* styling it normally carries is imported below. -->
  <div v-redirect-if-no-permission:DRIVERS>
    <loading-component :value="loading" />

    <v-dialog v-model="formDialog" max-width="760px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? $t("fleet.maintenance.editRecord") : $t("fleet.maintenance.addRecord") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeForm"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <!-- A posted record's money, date and vehicle are what the journal
               entry was built from; the server refuses to move them and the
               form must say so before the operator types. -->
          <div v-if="form.is_posted" class="cm-notice cm-notice--warn">
            {{ $t("fleet.maintenance.frozenHint") }}
          </div>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-autocomplete
                v-model="form.car_id"
                :items="carOptions"
                item-text="text"
                item-value="value"
                :label="$t('fleet.maintenance.car')"
                :disabled="form.is_posted"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model="form.service_date"
                type="date"
                :label="$t('fleet.maintenance.serviceDate')"
                :disabled="form.is_posted"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-select
                v-model="form.category"
                :items="categoryOptions"
                item-text="text"
                item-value="value"
                :label="$t('fleet.maintenance.category')"
              ></v-select>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model.number="form.cost"
                type="number"
                min="0"
                :label="$t('fleet.maintenance.cost')"
                :disabled="form.is_posted"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.vendor" :label="$t('fleet.maintenance.vendor')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model="form.odometer_km"
                type="number"
                min="0"
                :label="$t('fleet.maintenance.odometer')"
                :hint="$t('fleet.maintenance.odometerHint')"
                persistent-hint
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.invoice_no" :label="$t('fleet.maintenance.invoiceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-text-field v-model="form.notes" :label="$t('fleet.maintenance.notes')"></v-text-field>
            </v-flex>
          </v-layout>
          <div v-if="postingNote" class="cm-notice">{{ postingNote }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeForm">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitForm">
            {{ $t("finance.common.apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title class="error--text">{{ $t("fleet.maintenance.deleteTitle") }}</v-card-title>
        <v-card-text>
          <div class="cm-notice cm-notice--danger">{{ $t("fleet.maintenance.deleteWarning") }}</div>
          <div v-if="deleteTarget">
            {{ deleteTarget.car_label || deleteTarget.plate_number }} &mdash;
            {{ deleteTarget.service_date }} &mdash;
            {{ formatMoney(deleteTarget.cost) }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="deleteDialog = false">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="submitDelete">
            {{ $t("fleet.maintenance.deleteConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <div class="fin-header fin-header--sticky">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("fleet.maintenance.title") }}</span>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="openCreate">
            <v-icon small left>add</v-icon>
            {{ $t("fleet.maintenance.addRecord") }}
          </v-btn>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadRecords">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-autocomplete
            v-model="filters.car_id"
            :items="carOptions"
            item-text="text"
            item-value="value"
            :label="$t('fleet.maintenance.car')"
            clearable
            hide-details
            class="fin-field--wide"
          ></v-autocomplete>
          <v-select
            v-model="filters.category"
            :items="categoryFilterOptions"
            item-text="text"
            item-value="value"
            :label="$t('fleet.maintenance.category')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-text-field
            v-model="filters.from"
            type="date"
            :label="$t('fleet.maintenance.from')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.to"
            type="date"
            :label="$t('fleet.maintenance.to')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="filters.posted"
            :items="postedOptions"
            item-text="text"
            item-value="value"
            :label="$t('fleet.maintenance.postingState')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-text-field
            v-model="filters.search"
            :label="$t('fleet.maintenance.search')"
            hide-details
            clearable
            class="fin-field--wide"
            @keyup.enter="applyFilters"
          ></v-text-field>
          <v-btn small color="primary" @click="applyFilters">{{ $t("fleet.maintenance.apply") }}</v-btn>
          <v-btn small flat @click="resetFilters">{{ $t("fleet.maintenance.clearFilters") }}</v-btn>
        </div>
      </div>

      <!-- Named "shown", not "total": the server paginates and offers no
           aggregate, so claiming a register-wide total here would be a
           number nobody could reconcile. -->
      <div class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.rowsTotal") }}</span>
          <span class="fin-stat-value">{{ records.length }} / {{ totalItems }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.totalCost") }}</span>
          <span class="fin-stat-value">{{ formatMoney(shownCost) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.totalPosted") }}</span>
          <span class="fin-stat-value">{{ formatMoney(shownPostedCost) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.totalUnposted") }}</span>
          <span :class="shownUnpostedCost ? 'fin-stat-bad' : 'fin-stat-value'">
            {{ formatMoney(shownUnpostedCost) }}
          </span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label caption">{{ $t("fleet.maintenance.totalsHint") }}</span>
        </div>
      </div>

      <div v-if="missingAccounts.length" class="fin-alert-error">
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("fleet.maintenance.missingAccounts") }}: {{ missingAccounts.join(", ") }}
      </div>

      <!-- A toast is missed; the picker stays truncated for the whole session,
           so the warning has to stay on screen with it. -->
      <div v-if="carsTruncated" class="fin-alert-error">
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("fleet.maintenance.carsTruncated", { count: 200 }) }}
      </div>

      <div v-if="accountsLine" class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.expenseAccount") }}</span>
          <span class="fin-stat-value">{{ accountLabel(expenseAccount) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("fleet.maintenance.cashAccount") }}</span>
          <span class="fin-stat-value">{{ accountLabel(cashAccount) }}</span>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="records"
        :total-items="totalItems"
        :pagination.sync="pagination"
        :rows-per-page-items="[25, 50, 100]"
        :no-data-text="$t('fleet.maintenance.noRecords')"
      >
        <template v-slot:items="props">
          <tr>
            <td class="cm-ltr">{{ props.item.service_date || "-" }}</td>
            <td class="cm-ltr">{{ props.item.plate_number || "-" }}</td>
            <td>{{ categoryLabel(props.item) }}</td>
            <td>{{ props.item.vendor || "-" }}</td>
            <td class="cm-ltr">{{ formatMoney(props.item.cost) }}</td>
            <td class="cm-ltr">{{ props.item.odometer_km === null ? "-" : props.item.odometer_km }}</td>
            <td class="cm-ltr">{{ props.item.invoice_no || "-" }}</td>
            <td>
              <v-chip small :color="props.item.is_posted ? 'success' : 'warning'" text-color="white">
                {{
                  props.item.is_posted
                    ? $t("fleet.maintenance.journalEntry", { id: props.item.journal_entry_id })
                    : $t("fleet.maintenance.unposted")
                }}
              </v-chip>
              <div v-if="!props.item.is_posted && props.item.posting_error" class="caption error--text">
                {{ props.item.posting_error }}
              </div>
            </td>
            <td>
              <div class="cm-actions">
                <v-btn small flat icon @click.stop="openEdit(props.item)">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="!props.item.is_posted"
                  small
                  flat
                  icon
                  color="primary"
                  :loading="postingId === props.item.id"
                  @click.stop="retryPost(props.item)"
                >
                  <v-icon small>sync</v-icon>
                </v-btn>
                <v-btn
                  v-if="!props.item.is_posted"
                  small
                  flat
                  icon
                  color="error"
                  @click.stop="openDelete(props.item)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import LoadingComponent from "@/components/LoadingComponent";

// What the plate picker asks for, and what the endpoint itself caps at.
const CAR_LIMIT = 200;

export default {
  name: "CarMaintenance",
  title() {
    return this.$t("fleet.maintenance.pageTitle");
  },
  components: { LoadingComponent },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      postingId: null,
      formDialog: false,
      deleteDialog: false,
      deleteTarget: null,
      records: [],
      totalItems: 0,
      cars: [],
      carsTruncated: false,
      categories: [],
      expenseAccount: null,
      cashAccount: null,
      missingAccounts: [],
      postingNote: null,
      // Server-side paging and sorting. `sortBy` carries the clicked header's
      // `value`, and every sortable header below names a key the controller's
      // SORT_MAP knows, so an unmapped column can never reach the query.
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: "service_date",
        descending: true,
      },
      // Dedupes the initial double-fire: v-data-table emits update:pagination
      // as it mounts, which would otherwise duplicate the first request.
      lastRequest: null,
      filters: {
        car_id: null,
        category: null,
        from: null,
        to: null,
        posted: null,
        search: null,
      },
      form: this.emptyForm(),
    };
  },
  computed: {
    isEditing() {
      return !!this.form.id;
    },
    headers() {
      return [
        { text: this.$t("fleet.maintenance.serviceDate"), value: "service_date" },
        { text: this.$t("fleet.maintenance.plateNumber"), value: "plate_number" },
        { text: this.$t("fleet.maintenance.category"), value: "category" },
        { text: this.$t("fleet.maintenance.vendor"), value: "vendor" },
        { text: this.$t("fleet.maintenance.cost"), value: "cost" },
        { text: this.$t("fleet.maintenance.odometer"), value: "odometer_km" },
        { text: this.$t("fleet.maintenance.invoiceNo"), value: "invoice_no" },
        { text: this.$t("fleet.maintenance.postingState"), value: "is_posted", sortable: false },
        { text: this.$t("finance.common.actions"), value: "actions", sortable: false },
      ];
    },
    carOptions() {
      return this.cars.map(function (car) {
        return { text: car.label || car.plate_number, value: car.id };
      });
    },
    categoryOptions() {
      return this.categories.map(function (category) {
        return { text: this.categoryText(category.value, category.label_ar), value: category.value };
      }, this);
    },
    categoryFilterOptions() {
      return [{ text: this.$t("fleet.maintenance.allCategories"), value: null }].concat(
        this.categoryOptions
      );
    },
    postedOptions() {
      return [
        { text: this.$t("fleet.maintenance.allStates"), value: null },
        { text: this.$t("fleet.maintenance.posted"), value: 1 },
        { text: this.$t("fleet.maintenance.unposted"), value: 0 },
      ];
    },
    accountsLine() {
      return !!(this.expenseAccount || this.cashAccount);
    },
    shownCost() {
      return this.sumCost(this.records);
    },
    shownPostedCost() {
      return this.sumCost(
        this.records.filter(function (record) {
          return record.is_posted;
        })
      );
    },
    shownUnpostedCost() {
      return this.sumCost(
        this.records.filter(function (record) {
          return !record.is_posted;
        })
      );
    },
  },
  watch: {
    pagination: {
      handler() {
        this.loadRecords();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadOptions();
    this.loadCars();
    this.loadRecords();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        car_id: null,
        service_date: null,
        category: "periodic",
        cost: null,
        vendor: null,
        odometer_km: null,
        invoice_no: null,
        notes: null,
        is_posted: false,
      };
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    // Prefer the dashboard's own translation so an English session is not
    // handed an Arabic label; the server's label_ar is the fallback for a
    // category the accountant added without a front-end release.
    categoryText(value, fallbackAr) {
      var key = "fleet.maintenance.categories." + value;
      var translated = this.$t(key);

      return translated === key ? fallbackAr || value : translated;
    },
    categoryLabel(record) {
      return this.categoryText(record.category, record.category_label_ar);
    },
    accountLabel(account) {
      if (!account) {
        return "-";
      }

      return account.code + " - " + (account.name || "");
    },
    sumCost(rows) {
      return rows.reduce(function (sum, record) {
        return sum + Number(record.cost || 0);
      }, 0);
    },
    buildParams() {
      return {
        car_id: this.filters.car_id,
        category: this.filters.category,
        from: this.filters.from,
        to: this.filters.to,
        posted: this.filters.posted,
        search: this.filters.search,
        page: this.pagination.page,
        perPage: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy,
        descending: this.pagination.descending ? 1 : 0,
      };
    },
    loadRecords() {
      var params = this.buildParams();
      var signature = JSON.stringify(params);
      if (signature === this.lastRequest) {
        return;
      }
      this.lastRequest = signature;

      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.CAR_MAINTENANCE, { params: params })
        .then(
          function (response) {
            var page = (response.data && response.data.data) || {};
            this.records = page.data || [];
            this.totalItems = Number(page.total || 0);
          }.bind(this)
        )
        .catch(
          function (error) {
            // Clearing the signature matters: without it a failed request
            // would block the identical retry that fixes it.
            this.lastRequest = null;
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    loadOptions() {
      this.axios
        .get(this.api.ACTIONS.CAR_MAINTENANCE_OPTIONS)
        .then(
          function (response) {
            var payload = (response.data && response.data.data) || {};
            this.categories = payload.categories || [];
            this.expenseAccount = payload.expense_account || null;
            this.cashAccount = payload.cash_account || null;
            this.missingAccounts = payload.missing_accounts || [];
            this.postingNote = payload.posting_note_ar || null;
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedOptions")),
              "red"
            );
          }.bind(this)
        );
    },
    loadCars() {
      this.axios
        .get(this.api.ACTIONS.CAR_MAINTENANCE_CARS, { params: { limit: CAR_LIMIT } })
        .then(
          function (response) {
            this.cars = (response.data && response.data.data) || [];
            // The endpoint caps the list. A picker that silently stops at the
            // cap looks complete, and a clerk who cannot find a plate picks
            // the nearest one -- which is exactly how a repair lands against
            // the wrong vehicle. Say so instead.
            this.carsTruncated = this.cars.length >= CAR_LIMIT;
            if (this.carsTruncated) {
              window.getApp.$emit(
                "SHOW_TOAST",
                this.$t("fleet.maintenance.carsTruncated", { count: CAR_LIMIT }),
                "orange"
              );
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            // A swallowed failure here is indistinguishable from an empty
            // fleet: the picker just renders "no data" and the clerk reads
            // that as "my plate is not in the system". Say the request
            // failed, the same way loadRecords does.
            this.cars = [];
            this.carsTruncated = false;
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedCars")),
              "red"
            );
          }.bind(this)
        );
    },
    applyFilters() {
      // A filter change must return to page 1: staying on page 7 of a result
      // set that now has two pages shows an empty table.
      this.pagination.page = 1;
      this.loadRecords();
    },
    resetFilters() {
      this.filters = {
        car_id: null,
        category: null,
        from: null,
        to: null,
        posted: null,
        search: null,
      };
      this.applyFilters();
    },
    openCreate() {
      this.form = this.emptyForm();
      this.formDialog = true;
    },
    openEdit(record) {
      this.form = {
        id: record.id,
        car_id: record.car_id,
        service_date: record.service_date,
        category: record.category,
        // Money is stored in old-unit SYP; the input edits NEW units, so it is
        // divided here and multiplied back by toStoredAmount on save.
        cost: record.cost === null || record.cost === undefined ? null : Number(record.cost) / 100,
        vendor: record.vendor,
        odometer_km: record.odometer_km,
        invoice_no: record.invoice_no,
        notes: record.notes,
        is_posted: !!record.is_posted,
      };
      this.formDialog = true;
    },
    closeForm() {
      this.formDialog = false;
      this.form = this.emptyForm();
    },
    submitForm() {
      if (!this.form.car_id || !this.form.service_date || !this.form.category || this.form.cost === null || this.form.cost === "") {
        window.getApp.$emit("SHOW_TOAST", this.$t("fleet.maintenance.requiredFields"), "red");
        return;
      }

      var payload = {
        category: this.form.category,
        vendor: this.form.vendor || null,
        // Left null when empty on purpose: an absent workshop reading must
        // stay absent, and a 0 would corrupt every distance figure.
        odometer_km:
          this.form.odometer_km === null || this.form.odometer_km === "" ? null : Number(this.form.odometer_km),
        invoice_no: this.form.invoice_no || null,
        notes: this.form.notes || null,
      };

      // The server refuses to move a posted record's money, date or vehicle;
      // omitting them entirely keeps a no-op edit of the notes from tripping
      // that guard on a value the operator never touched.
      if (!this.form.is_posted) {
        payload.car_id = this.form.car_id;
        payload.service_date = this.form.service_date;
        payload.cost = this.toStoredAmount(this.form.cost);
      }

      this.saving = true;
      var request = this.isEditing
        ? this.axios.put(this.api.ACTIONS.CAR_MAINTENANCE_RECORD(this.form.id), payload)
        : this.axios.post(this.api.ACTIONS.CAR_MAINTENANCE, payload);

      request
        .then(
          function (response) {
            var record = (response.data && response.data.data) || {};
            window.getApp.$emit("SHOW_TOAST", this.$t("fleet.maintenance.saved"), "green");
            // Posting is fail-soft: the record saved either way, so an
            // unposted result is a warning next to the success, not an error
            // that hides the fact the repair was recorded.
            if (record.posting_error) {
              window.getApp.$emit(
                "SHOW_TOAST",
                this.$t("fleet.maintenance.postingFailed") + ": " + record.posting_error,
                "orange"
              );
            }
            this.closeForm();
            this.reload();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedSave")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    openDelete(record) {
      this.deleteTarget = record;
      this.deleteDialog = true;
    },
    submitDelete() {
      if (!this.deleteTarget) {
        return;
      }

      this.deleting = true;
      this.axios
        .delete(this.api.ACTIONS.CAR_MAINTENANCE_RECORD(this.deleteTarget.id))
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("fleet.maintenance.deleted"), "green");
            this.deleteDialog = false;
            this.deleteTarget = null;
            this.reload();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedDelete")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.deleting = false;
          }.bind(this)
        );
    },
    retryPost(record) {
      this.postingId = record.id;
      this.axios
        .post(this.api.ACTIONS.CAR_MAINTENANCE_POST(record.id))
        .then(
          function (response) {
            var updated = (response.data && response.data.data) || {};
            if (updated.is_posted) {
              window.getApp.$emit("SHOW_TOAST", this.$t("fleet.maintenance.postRetried"), "green");
            } else {
              window.getApp.$emit(
                "SHOW_TOAST",
                updated.posting_error || this.$t("fleet.maintenance.failedPost"),
                "orange"
              );
            }
            this.reload();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("fleet.maintenance.failedPost")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.postingId = null;
          }.bind(this)
        );
    },
    // A write does not change the request shape, so the dedupe guard has to
    // be released explicitly or the reload would be skipped as a duplicate.
    reload() {
      this.lastRequest = null;
      this.loadRecords();
    },
  },
};
</script>

<!-- Shared compact finance styling. Imported directly because this page does
     not render <finance-section-nav />, which is what carries it elsewhere. -->
<style src="@/assets/finance-compact.css"></style>

<style scoped>
.cm-notice {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 10px;
}
.cm-notice--warn {
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff8e1;
  border-inline-start: 4px solid #f9a825;
  font-weight: 600;
}
.cm-notice--danger {
  padding: 8px 12px;
  border-radius: 4px;
  background: #ffebee;
  color: #b71c1c;
  border-inline-start: 4px solid #c62828;
  font-weight: 700;
}
.cm-actions {
  display: flex;
  align-items: center;
}
/* Plates, dates and amounts read left-to-right inside the Arabic layout. */
.cm-ltr {
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}
</style>
